// One conduct record's description: ticked presets joined with "; ", then " - " and the note.
import { REASON_PRESETS } from '../../config/violation-reasons.ts';

const REASON_SEPARATOR = '; ';
const NOTE_SEPARATOR = ' - ';
const ALL_PRESETS: readonly string[] = [...REASON_PRESETS.commendation, ...REASON_PRESETS.violation];

export interface ConductEntryLines {
  reasons: string[];
  note: string;
}

/** Ticked reasons in checklist order (not tick order), so the saved text reads like the form. */
export function composeDescription(presets: readonly string[], ticked: readonly string[], note: string): string {
  const reasons = presets.filter((reason) => ticked.indexOf(reason) >= 0).join(REASON_SEPARATOR);
  const trimmed = note.trim();
  return trimmed ? `${reasons}${NOTE_SEPARATOR}${trimmed}` : reasons;
}

function leadingPreset(rest: string): string | null {
  for (const preset of ALL_PRESETS) {
    if (rest.indexOf(preset) !== 0) continue;
    const tail = rest.slice(preset.length);
    if (tail === '' || tail.indexOf(REASON_SEPARATOR) === 0 || tail.indexOf(NOTE_SEPARATOR) === 0) return preset;
  }
  return null;
}

/** Splits a stored description back into its reasons and note; free text from QLT web splits on "; " only. */
export function parseDescription(description: string): ConductEntryLines {
  const text = (description || '').trim();
  const reasons: string[] = [];
  let rest = text;
  for (let preset = leadingPreset(rest); preset !== null; preset = leadingPreset(rest)) {
    reasons.push(preset);
    rest = rest.slice(preset.length);
    if (rest.indexOf(REASON_SEPARATOR) !== 0) break;
    rest = rest.slice(REASON_SEPARATOR.length);
  }
  if (reasons.length === 0) {
    return { reasons: text.split(REASON_SEPARATOR).map((part) => part.trim()).filter(Boolean), note: '' };
  }
  if (rest.indexOf(NOTE_SEPARATOR) === 0) return { reasons, note: rest.slice(NOTE_SEPARATOR.length).trim() };
  // a preset run cut short by free text: keep that text as its own line rather than drop it
  return rest.trim() ? { reasons: reasons.concat(rest.trim()), note: '' } : { reasons, note: '' };
}
